import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, MapPin, Euro, Plus, X } from "lucide-react";
import { TransportationActivity } from "../models/activity.model";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { DatePicker } from "@/components/ui/date-picker";

interface TransportationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<TransportationActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<TransportationActivity>;
  itineraryId: string;
}

const TransportationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate }: TransportationModalProps) => {
  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() => 
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<TransportationActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    transport_type: 'train',
    company: '',
    travels: [
      {
        address: '',
        date: undefined,
        time: '',
      },
      {
        address: '',
        date: undefined,
        time: '',
      }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const addTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []).slice(0, index + 1),
        {
          address: '',
          date: '',
          time: ''
        },
        ...(prev.travels || []).slice(index + 1)
      ]
    }));
  };

  const removeTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateTravel = (index: number, field: keyof TransportationActivity['travels'][0], value: string) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((travel, i) =>
        i === index ? { ...travel, [field]: value } : travel
      ) || []
    }));
  };

  const validateForm = () => {
    const requiredFields = ['title', 'link', 'company', 'cost'];
    const newErrors: Record<string, boolean> = {};

    // Validate main form fields
    requiredFields.forEach(field => {
      const value = formData[field as keyof TransportationActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    // Validate travels array
    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      // Validate each travel's required fields
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['address', 'date', 'time'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });
      });
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        type: 'transportation' as const,
        date: formData.travels ? formData.travels[0].date : undefined
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} Transportation
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full justify-between">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" form="transportation-form">
            Confirm
          </Button>
        </div>
      }
    >
      <form id="transportation-form" onSubmit={handleSubmit} className="space-y-4 p-6">
        {/* Include Price Switch */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div>
            <label className="text-sm font-medium text-gray-700">Include Price</label>
            <p className="text-xs text-gray-500">Included in the total price of the package</p>
          </div>
          <Switch
            checked={formData.include_price || false}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
          />
        </div>

        {/* General Information Card */}
        <Card>
          <CardHeader>
            <CardTitle>General Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* First row: Title and Link */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Input
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Transportation Name"
                  hasError={errors.title}
                />
              </div>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                <Input
                  id="link"
                  value={formData.link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="Transportation link"
                  className="pl-10"
                />
              </div>
            </div>

            {/* Second row: Transport Type and Company */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Select
                  value={formData.transport_type || 'train'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, transport_type: value as TransportationActivity['transport_type'] }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select transport type" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { value: 'train', label: 'Train' },
                      { value: 'bus', label: 'Bus' },
                      { value: 'car', label: 'Car' },
                      { value: 'taxi', label: 'Taxi' },
                      { value: 'metro', label: 'Metro' },
                      { value: 'other', label: 'Other' }
                    ].map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                <Input
                  id="company"
                  value={formData.company || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Company"
                  className="pl-10"
                  hasError={errors.company}
                />
              </div>
            </div>

            {/* Travels Section */}
            <div className="space-y-4">
              <div className="text-sm font-medium text-gray-700">Travel Route</div>
              <div className="space-y-4">
                {formData.travels?.map((travel, index) => (
                  <div key={index}>


                    {/* Address, Date, Time */}
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="w-20 text-sm font-medium">
                        {index === 0 ? 'From' : index === formData.travels!.length - 1 ? 'To' : 'Stop'}
                      </div>
                      <div className="flex-1">
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                          <Input
                            value={travel.address}
                            onChange={(e) => updateTravel(index, 'address', e.target.value)}
                            placeholder={index === 0 ? 'Departure location' : index === formData.travels!.length - 1 ? 'Arrival location' : 'Stop location'}
                            className="pl-10"
                            hasError={errors[`travel_${index}_address`]}
                          />
                        </div>
                      </div>
                      <div className="w-52">
                        <DatePicker
                          value={travel.date}
                          onSelect={(date) => updateTravel(index, 'date', date)}
                          placeholder="Date"
                          hasError={errors[`travel_${index}_date`]}
                          minDate={itineraryStartDate}
                          maxDate={itineraryEndDate}
                        />
                      </div>
                      <div className="w-30">
                        <Input
                          type="time"
                          value={travel.time}
                          onChange={(e) => updateTravel(index, 'time', e.target.value)}
                          placeholder="HH:MM"
                          hasError={errors[`travel_${index}_time`]}
                        />
                      </div>
                      <div className="flex items-center justify-end">
                        {formData.travels && formData.travels.length > 2 && index !== 0 && index !== formData.travels.length - 1 && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => removeTravel(index)}
                            className="p-3 mr-3"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                  </div>
                ))}
              </div>

              {/* Add Stop Button - after all travels */}
              <div className="flex justify-center pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => addTravel(formData.travels!.length - 2)}
                  className="w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add stop
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price Card */}
        <Card>
          <CardHeader>
            <CardTitle>Price</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.cost || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                    placeholder="Amount (only visible to you)"
                    className="pl-10"
                    hasError={errors.cost}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description Card */}
        <Card>
          <CardHeader>
            <CardTitle>Note</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Share client preferences with Place2Be, such as seat preferences, luggage requirements, or special requests."
              rows={6}
            />
          </CardContent>
        </Card>

        {/* Documentation Card */}
        <Card>
          <CardHeader>
            <CardTitle>Documentation</CardTitle>
          </CardHeader>
          <CardContent>
            <UploadMultiFiles
              files={formData.documentation || []}
              onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
              label="Upload boarding pass when available"
              maxFiles={5}
              itineraryId={itineraryId}
              activityId={activityId}
            />
          </CardContent>
        </Card>

      </form>
    </Modal>
  );
};

export default TransportationModal;
